"use client";

import React, { useState, useCallback, useMemo, useEffect, useContext } from "react";

import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import dynamic from "next/dynamic";
import { useParams, usePathname } from "next/navigation";

import { GlobalPathParams } from "~/app/[locale]/types";
import { FooterProps } from "~/bff/components/Footer";
import { CountrySelectorPropsExtended } from "~/components/country-selector/component";
import { DEFAULT_LOCALE } from "~/constants/i18n";
import { Routes } from "~/constants/request";
import { AuthContext } from "~/context/auth/context";
import { DictionaryLabelContext } from "~/context/dictionary-label/context";
import { DictionaryKeys } from "~/context/dictionary-label/types";
import { ShoppingListContext } from "~/context/shopping-list/context";
import { getCountryCode } from "~/helpers/country/get-country-code";
import { CountryCode } from "~/helpers/country/types";
import { importComponentByName } from "~/helpers/import-component-by-name";
import {
  getCountryLangFromLocale,
  getLocaleByContryAndLanguage,
} from "~/helpers/locales";
import { getTestAutomationProps } from "~/helpers/test-automation-props";

import { ShoppingListPopupProps } from "../shopping-list-popup/component";

import { Copyright } from "./components/copyright/component";
import { GroupedLinks } from "./components/grouped-links/component";
import { SocialNetworkLinks } from "./components/social-network-links/component";
import { FOOTER_CLASSES } from "./constants";
import { convertFooterToProps } from "./helpers/convert-footer-to-props";
import { CountrySelectorWrapper, FooterRootContainer } from "./styled";

const CountrySelector = dynamic<CountrySelectorPropsExtended>(async () =>
  importComponentByName(
    import("~/components/country-selector/component"),
    "CountrySelector",
  ),
);
const ShoppingListPopup = dynamic<ShoppingListPopupProps>(async () =>
  importComponentByName(
    import("../shopping-list-popup/component"),
    "ShoppingListPopup",
  ),
);

export const Footer: React.FC<FooterProps> = (data) => {
  const convertedProps = useMemo(() => convertFooterToProps(data), [data]);

  const { account } = useContext(AuthContext) ?? {};

  const { isLimitPopupOpen, handleClosePopup } =
    useContext(ShoppingListContext) ?? {};

  const dictionary = useContext(DictionaryLabelContext);

  const pathname = usePathname();
  const { locale } = useParams<GlobalPathParams>();
  const initialLanguageCode = getCountryLangFromLocale(locale);
  const countryCode2 = getCountryCode(locale);
  const [selectedCountry, setSelectedCountry] = useState<CountryCode | null>(
    countryCode2,
  );
  const [selectedLanguage, setSelectedLanguage] =
    useState<string>(initialLanguageCode);

  useEffect(() => {
    setSelectedCountry(countryCode2 as CountryCode);
    setSelectedLanguage(initialLanguageCode);
  }, [countryCode2, initialLanguageCode]);

  const shouldOpenAllLinksInNewTab = useMemo(() => {
    return pathname.includes(Routes.PROFILE);
  }, [pathname]);

  const handleChange = useCallback(
    (
      countryCode: CountryCode | undefined,
      countryLanguageCode: string | undefined,
    ) => {
      setSelectedCountry(countryCode ?? null);

      const foundCountry = convertedProps?.countriesData?.countries?.find(
        (country) =>
          country?.countryKey === countryCode &&
          countryLanguageCode === country?.languageCode,
      );

      const hasSelectorOuterLink =
        typeof foundCountry?.link?.url === "string" &&
        foundCountry?.link?.url.includes("http");

      if (hasSelectorOuterLink && typeof foundCountry?.link?.url === "string") {
        open(foundCountry.link.url, "_self");
        return;
      }

      if (typeof foundCountry?.languageCode === "string") {
        setSelectedLanguage(foundCountry?.languageCode);

        const newLocale =
          typeof foundCountry?.countryKey === "string"
            ? getLocaleByContryAndLanguage(
                foundCountry?.languageCode,
                foundCountry?.countryKey,
              )
            : DEFAULT_LOCALE;

        // eslint-disable-next-line no-restricted-globals
        open(`${window?.location.origin}/${newLocale}`, "_self");
      }
    },
    [convertedProps?.countriesData?.countries],
  );

  return (
    <FooterRootContainer
      {...getTestAutomationProps("footer")}
      component="footer"
      id="footer"
      role="contentinfo"
    >
      <Container className={FOOTER_CLASSES.container}>
        <Grid
          className={FOOTER_CLASSES.contentWrapper}
          container
          justifyContent="center"
        >
          <SocialNetworkLinks socialLinks={convertedProps?.socialLinks ?? []} />

          <GroupedLinks
            groupedLinks={convertedProps?.footerNav ?? []}
            selectedCountry={selectedCountry ?? undefined}
            selectedLanguage={selectedLanguage}
            setSelectedCountry={handleChange}
            countries={convertedProps?.countriesData?.countries ?? []}
            title={
              convertedProps?.countriesData?.countrySelectorHeader?.title ??
              undefined
            }
            description={
              convertedProps?.countriesData?.countrySelectorHeader?.description ??
              undefined
            }
            shouldOpenAllLinksInNewTab={shouldOpenAllLinksInNewTab}
          />

          <CountrySelectorWrapper item xs={12} id="country-selector">
            <Box mt={1} mb={4}>
              <CountrySelector
                countries={convertedProps?.countriesData?.countries}
                value={selectedCountry ?? undefined}
                selectedLanguage={selectedLanguage}
                onChange={handleChange}
                title={
                  convertedProps?.countriesData?.countrySelectorHeader?.title ??
                  undefined
                }
                description={
                  convertedProps?.countriesData?.countrySelectorHeader
                    ?.description ?? undefined
                }
                testAutomationId="country-selector-popup-button-mobile"
              />
            </Box>
          </CountrySelectorWrapper>
        </Grid>
      </Container>

      <Copyright copyrightText={convertedProps?.copyright ?? ""} />
      <ShoppingListPopup
        open={isLimitPopupOpen}
        handlerClosePopup={handleClosePopup}
        title={dictionary?.[DictionaryKeys.LimitationModalWindowTitle]}
        description={dictionary?.[DictionaryKeys.LimitationModalWindowDescription]}
        buttonText={dictionary?.[DictionaryKeys.LimitationModalWindowOkButton]}
      />
      {account && (
        <Box
          className={FOOTER_CLASSES.evergageData}
          data-evergage-email={account?.email}
          data-evergage-user-id={account?.personContactId}
          data-cs-mask
        />
      )}
    </FooterRootContainer>
  );
};
